import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, ElementRef, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, input, viewChild } from '@angular/core';
import { ThemeService } from '../../../../services/theme.service';
import { Icons } from '../../../../util/Icons';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { SessionService } from '../../../../services/session.service';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { finalize } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { RealEstate, RealEstateService } from '@eadsysteme/ewsrealestateclient-v2';
import { SnackbarService } from '../../../../services/snackbar.service';
import { NumberUtil } from 'src/app/util/NumberUtil';
import { DateUtil } from 'src/app/util/DateUtil';
import { Consumption, ConsumptionCalcService, ConsumptionRequest, EnergyEfficiencySettingService } from '@eadsysteme/energyefficiencyclient-v1';
import { saveAs } from 'file-saver';
import { BreadCrumbService } from '../../../../../@vex/components/breadcrumbs/BreadCrumbService';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { TenantUnit } from '@eadsysteme/ewsrealestateclient-v1';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_MONTHYEAR_FORMATS } from '../../../../util/material-dayjs-adapter';
import { MatDialog } from '@angular/material/dialog';
import { fadeInUp400ms } from '../../../../../@vex/animations/fade-in-up.animation';
import { LayoutService } from '../../../../../@vex/services/layout.service';
import { ActivatedRoute } from '@angular/router';
import { NgClass, NgStyle, NgTemplateOutlet, DecimalPipe } from '@angular/common';
import { DefaultClassDirective, DefaultStyleDirective } from '@ngbracket/ngx-layout/extended';
import { MatIcon } from '@angular/material/icon';
import { NgTooltipModule } from '@silmar/ng-tooltip';
import { PageLayoutComponent } from '../../../../../@vex/components/page-layout/page-layout.component';
import { BarChartModule } from '@swimlane/ngx-charts';

@Component({
	selector: 'chart-display',
	templateUrl: './chart-display.component.html',
	styleUrls: ['./chart-display.component.scss'],
	animations: [fadeInUp400ms],
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		[NumberUtil],
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_MONTHYEAR_FORMATS },
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		NgClass,
		DefaultClassDirective,
		NgStyle,
		DefaultStyleDirective,
		MatIcon,
		NgTooltipModule,
		PageLayoutComponent,
		BarChartModule,
		NgTemplateOutlet,
		DecimalPipe,
	],
})
export class ChartDisplayComponent implements OnChanges {
	private readonly themeService = inject(ThemeService);
	private readonly translocoLocaleService = inject(TranslocoLocaleService);
	private readonly translocoService = inject(TranslocoService);
	private readonly consumptionCalcService = inject(ConsumptionCalcService);
	private readonly energyEfficiencySettingsService = inject(EnergyEfficiencySettingService);
	layoutService = inject(LayoutService);
	sessionService = inject(SessionService);
	dateUtilService = inject(DateUtil);
	dialog = inject(MatDialog);
	snackBar = inject(SnackbarService);
	icons = inject(Icons);
	private readonly realEstateService = inject(RealEstateService);
	numberUtil = inject(NumberUtil);
	private readonly activatedRoute = inject(ActivatedRoute);
	breadCrumbService = inject(BreadCrumbService);

	private readonly destroyRef = inject(DestroyRef);
	date = new UntypedFormControl(dayjs());

	readonly chartComponent = viewChild<ElementRef>('chartComponent');
	// chart Config
	diagramSize: [number, number] = [550, 250];
	showXAxis = true;
	showYAxis = true;
	showYAxisLabel = true;
	primaryRgbColor = '#5c77ff';
	grey = '#b1b1b1';
	lightGrey = '#dedede';
	customColors = [];
	isMobile = false;
	consumption$: Observable<Array<Consumption>>;
	chartDataHeating;
	chartDataWarmwater;
	chartDataCooling;
	chartDataColdwater;
	chartDataHeatingWarmwater;
	costTypeEnum = Consumption.CostTypeEnum;
	model: any = {};
	dateSelect: Date[];
	refreshOptions: string[] = [];
	printSquareMeterConsumption = false;
	locale: string;
	activeLang: string;
	finishedLoading = true;
	dateMismatch = false;
	consumptions: Array<Consumption>;
	currentConsumptionName = '';
	lastYearConsumptionName = '';
	lastMonthConsumptionName = '';
	public yAxisTickFormattingFn = this.yAxisTickFormatting.bind(this);
	public xAxisTickFormattingFn = this.yAxisTickFormatting.bind(this);
	readonly datePickerElement = viewChild('picker');
	barChartDimensions: [number, number];
	readonly inputDate = input<Date>(undefined);
	@Input() currentRealEstate!: RealEstate;
	readonly currentTenantUnit = input.required<TenantUnit>();

	constructor() {
		const translocoLocaleService = this.translocoLocaleService;

		this.locale = translocoLocaleService.getLocale();
		this.activeLang = this.translocoService.getActiveLang();
		this.setBreadCrumbs();
		this.setSizeIfMobile();
		this.setPrimaryColorToChart();
		// TODO move in localizations if even needed
		this.refreshOptions.push('UVI dieser Nutzereinheit neu laden');
		this.refreshOptions.push('UVI dieser Liegenschaft neu laden');
		this.setChartStyleByDevice();

		if (!this.currentRealEstate) {
			const realEstateId = this.activatedRoute.snapshot.paramMap.get('realEstateId');
			this.realEstateService
				.getRealEstate(this.sessionService.effectiveArea, realEstateId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (realEstate) => {
						this.currentRealEstate = realEstate;
						this.loadRealEstateSettings();
					},
				});
		} else {
			this.loadRealEstateSettings();
		}
	}

	get selectedDate() {
		return this.inputDate();
	}

	private static roundValues(energyValue: Consumption): Consumption {
		energyValue.currentConsumption.value = ChartDisplayComponent.nanCheck(Number.parseInt(energyValue.currentConsumption?.value?.toFixed(), 10));
		energyValue.lastMonthConsumption.value = ChartDisplayComponent.nanCheck(Number.parseInt(energyValue.lastMonthConsumption?.value?.toFixed(), 10));
		energyValue.lastYearConsumption.value = ChartDisplayComponent.nanCheck(Number.parseInt(energyValue.lastYearConsumption?.value?.toFixed(), 10));

		return energyValue;
	}

	private static nanCheck(toFixed: number | undefined) {
		if (isNaN(toFixed)) {
			return undefined;
		}
		return toFixed;
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.reloadData();
	}

	scaleBy0(value: number) {
		return this.numberUtil.roundConsumptionValues(value, 0);
	}

	setBarStyleByDevice(bar: string) {
		if (this.layoutService.isMobile()) {
			return 'dataTextLabel ' + bar + 'Mobile';
		} else if (this.layoutService.isTablet()) {
			return 'dataTextLabel ' + bar + 'Tablet';
		} else if (this.layoutService.isTabletLandscape()) {
			return 'dataTextLabel ' + bar + 'TabletLandscape';
		} else {
			return 'dataTextLabel ' + bar + 'Desktop';
		}
	}

	performPrint(billable: boolean) {
		try {
			const printDate = dayjs(this.selectedDate).isAfter(dayjs()) ? dayjs() : dayjs(this.selectedDate);
			this.consumptionCalcService
				.getConsumptionReport(this.currentRealEstate.area, this.currentRealEstate.id, this.currentTenantUnit().id, billable, {
					readingDate: printDate.format('YYYY-MM-DD'),
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((report) => {
					if (report) {
						const blob = new Blob([report], { type: 'application/pdf' });
						const fileName = this.setFileName();
						saveAs(blob, fileName);
					}
				});
		} catch (exception) {
			if (this.sessionService.isDevRole()) {
				console.log('an error occurred while generating pdf: ' + exception.toString());
			}
		}
	}

	yAxisTickFormatting(value) {
		return value;
	}

	formatDataLabel(value) {
		if (value === undefined || value === null) {
			return '';
		} else {
			return value + ' kWh';
		}
	}

	formatDataLabelWater(value) {
		if (value === undefined || value === null) {
			return '';
		} else if (value == 0) {
			return '0 m³';
		} else {
			return (Math.round(value * 100) / 100).toFixed(2) + ' m³';
		}
	}

	setFileName(): string {
		const activeLang = this.translocoService.getActiveLang();
		const cIY = activeLang === 'en' ? 'cIY' : 'uVI';
		const month = '_' + dayjs(this.selectedDate).locale(activeLang).format('MMMM');
		const year = '_' + dayjs(this.selectedDate).year();
		return cIY + month + year + '.pdf';
	}

	async printUvi() {
		this.performPrint(true);
	}

	getLastMonth(date: Date): string {
		const dateMinusMonth = dayjs(date).subtract(1, 'month').toDate();
		return dateMinusMonth.toLocaleString(this.translocoLocaleService.getLocale(), { month: 'long' });
	}

	getCurrentYear(date: Date): string {
		return dayjs(date).format('YYYY');
	}

	getLastYear(date: Date): string {
		const dateMinusYear = dayjs(date).subtract(1, 'year').toDate();
		return dateMinusYear.toLocaleString(this.translocoLocaleService.getLocale(), { year: 'numeric' });
	}

	reloadData() {
		this.customColors = [];
		this.currentConsumptionName = '';
		this.lastYearConsumptionName = '';
		this.lastMonthConsumptionName = '';
		this.finishedLoading = false;
		this.getConsumptionValues();
	}

	private setChartStyleByDevice() {
		if (this.layoutService.isMobile()) {
			this.barChartDimensions = [0, 400];
		} else if (this.layoutService.isTablet()) {
			this.barChartDimensions = [0, 400];
		} else if (this.layoutService.isTabletLandscape()) {
			this.barChartDimensions = [0, 400];
		} else {
			this.barChartDimensions = [0, 400];
		}
	}

	private setBreadCrumbs() {
		this.sessionService
			.getAllPermissionsOfLoggedInUser()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((permissionObjects) => {
				if (permissionObjects.length === 1 && permissionObjects[0].permissionType === PermissionObject.PermissionTypeEnum.OWNER) {
					return;
				}
			});

		this.breadCrumbService.addBreadCrumb({
			translationKey: 'sideboard.uvi',
			icon: this.icons.get('uvi'),
		});
	}

	private setSizeIfMobile() {
		this.isMobile = this.layoutService.isMobile() || this.layoutService.isLtLg();

		if (this.layoutService.isMobile()) {
			const width = window.innerWidth * 0.7;
			const height = width * 0.8;
			this.diagramSize = [width, height];
		} else {
			this.diagramSize = [550, 250];
		}
	}

	private setPrimaryColorToChart() {
		this.primaryRgbColor = this.themeService.getPrimaryColorFromCssVar();
	}

	private fillCharts(energyValue: any) {
		switch (energyValue.costType) {
			case this.costTypeEnum.HEATING:
				energyValue = ChartDisplayComponent.roundValues(energyValue);
				this.chartDataHeating = this.fillChartDataArray(energyValue);
				break;
			case this.costTypeEnum.WARMWATER:
				energyValue = ChartDisplayComponent.roundValues(energyValue);
				this.chartDataWarmwater = this.fillChartDataArray(energyValue);
				break;
			case this.costTypeEnum.COLDWATER:
				this.chartDataColdwater = this.fillChartDataArray(energyValue);
				break;
			case this.costTypeEnum.COOLING:
				energyValue = ChartDisplayComponent.roundValues(energyValue);
				this.chartDataCooling = this.fillChartDataArray(energyValue);
				break;
			case this.costTypeEnum.HEATING_WARMWATER:
				energyValue = ChartDisplayComponent.roundValues(energyValue);
				this.chartDataHeatingWarmwater = this.fillChartDataArray(energyValue);
				break;
		}
	}

	private fillChartDataArray(energyValue: Consumption) {
		const readingDate = new Date(energyValue.readingDate);
		const currentMonth = this.getCurrentMonth(readingDate);
		const lastMonth = this.getLastMonth(readingDate);
		const lastYear = this.getLastYear(readingDate);
		const currentYear = this.getCurrentYear(readingDate);
		this.lastYearConsumptionName = currentMonth + ' ' + lastYear;
		this.lastMonthConsumptionName = lastMonth.startsWith('D') ? lastMonth + ' ' + lastYear : lastMonth + ' ' + currentYear;
		this.currentConsumptionName = currentMonth + ' ' + currentYear;
		return [
			{
				name: this.lastYearConsumptionName,
				value: energyValue.lastYearConsumption.value == null ? 0 : energyValue.lastYearConsumption.value,
			},
			{
				name: this.lastMonthConsumptionName,
				value: energyValue.lastMonthConsumption.value == null ? 0 : energyValue.lastMonthConsumption.value,
			},
			{
				name: this.currentConsumptionName,
				value: energyValue.currentConsumption.value == null ? 0 : energyValue.currentConsumption.value,
			},
		];
	}

	private addCustomColors() {
		this.customColors.push({ name: this.lastYearConsumptionName, value: this.lightGrey });
		this.customColors.push({ name: this.lastMonthConsumptionName, value: this.grey });
		this.customColors.push({ name: this.currentConsumptionName, value: this.primaryRgbColor });
	}

	private getConsumptionValues() {
		this.finishedLoading = false;
		if (!this.selectedDate) {
			this.finishedLoading = true;
			return;
		}
		this.dateMismatch = false;
		const consumptionRequest: ConsumptionRequest = {};
		consumptionRequest.readingDate = new Date(this.selectedDate).toJSON();
		this.consumption$ = this.consumptionCalcService.calculateConsumptions(
			this.currentRealEstate?.area,
			this.currentRealEstate?.id,
			this.currentTenantUnit()?.id,
			consumptionRequest
		);
		this.consumption$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.pipe(finalize(() => (this.finishedLoading = true)))
			.subscribe({
				next: (consumptions) => {
					this.consumptions = consumptions;
					this.dateMismatch = this.getCurrentMonth(new Date(consumptions[0]?.readingDate)) !== this.getCurrentMonth(this.selectedDate);
					if (this.dateMismatch) {
						return;
					}
					for (const consumption of consumptions) {
						this.fillCharts(consumption);
					}
					this.addCustomColors();
				},
				error: () => this.snackBar.error(this.translocoService.translate('errors.consumptionData')),
			});
	}

	/** Monat inklusive Jahr - der reine Monatsname würde Januar 2025 und Januar 2026 gleichsetzen. */
	private getCurrentMonth(date: Date): string {
		return dayjs(date).format('YYYY-MM');
	}

	private loadRealEstateSettings() {
		this.energyEfficiencySettingsService.getRealEstateSetting(this.currentRealEstate.area, this.currentRealEstate.id).subscribe((value) => {
			this.printSquareMeterConsumption = value.printSquareMeterConsumption;
		});
	}
}
